import _ from "lodash";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "src/store";
import { Overlay } from "src/types/osbuilder";
import { useFetchProductByIdQuery, useUpdateProductMutation } from "src/ui/containers/dashboard/products/api";
import { makeStyles } from "tss-react/mui";

import { closeDetails } from "@dashboard/devices/store";
import { AndroidModule, Product } from "@dashboard/products/types";
import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { Box, Chip, Theme, Typography } from "@mui/material";
import { PermissionService } from "@services/permissionService/permissionService";
import { SecondaryButton } from "@shared/CustomButton";
import { QueryBuilderField } from "@shared/form/QueryBuilderField/QueryBuilderField";

import { PaneElement } from "../shared/PaneElement";

import { AddApplicationDialog } from "./AddApplicationDialog/AddApplicationDialog";
import { AddDownloadableFileDialog } from "./AddDownloadableFileDialog";
import { AddExecutableDialog } from "./AddExecutableDialog";
import { AddOverlayDialog } from "./AddOverlayDialog";
import { BootAnimationDialog } from "./BootAnimationDialog";
import { ExcludeModuleDialog } from "./ExcludeModuleDialog";
import { IncludeModuleDialog } from "./IncludeModuleDialog";
import {
    CustomOverlayCard,
    DownloadableFileCard,
    ExistingModuleCard,
    PrebuiltApplicationCard,
    PrebuiltBootAnimationCard,
    PrebuiltExecutableCard,
    RemoveOverrideCard,
} from "./ModuleCard";
import Utils from "./ModulePanel.utils";

const useStyles = makeStyles()((theme: Theme) => ({
    contentBox: { flex: 1, overflowY: "auto", padding: theme.spacing(6), paddingTop: 0 },
    header: { display: "flex", flexDirection: "column" },
    searchContainer: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(8),
    },
    tagsContainer: { display: "flex", flexDirection: "row", gap: theme.spacing(1) },
    gridContainer: {
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gap: theme.spacing(4),
    },
    searchContainerBox: { paddingLeft: theme.spacing(3), paddingRight: theme.spacing(3) },
    separator: {
        height: "1px",
        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
        marginTop: theme.spacing(6),
        marginBottom: theme.spacing(6),
    },
}));
type Props = {
    productId: number;
};
export const ModulePanel = (props: Props) => {
    const [openAddOwnApplicationDialog, setOpenAddOwnApplicationDialog] = React.useState(false);
    const [openAddOwnExecutableDialog, setOpenAddOwnExecutableDialog] = React.useState(false);
    const [openBootAnimationDialog, setOpenBootAnimationDialog] = React.useState(false);
    const [openIncludeSystemPackageDialog, setOpenIncludeSystemPackageDialog] = React.useState(false);
    const [openExcludeSystemPackageDialog, setOpenExcludeSystemPackageDialog] = React.useState(false);
    const [openAddDownloadableFileDialog, setOpenAddDownloadableFileDialog] = React.useState(false);
    const [openAddOverlayDialog, setOpenAddOverlayDialog] = React.useState(false);

    const { data: product } = useFetchProductByIdQuery(props.productId);

    const [selectedTag, setSelectedTag] = React.useState<string>(
        product?.customBuildConfig?.modules.length || product?.customBuildConfig?.overlays?.length ? "Added" : "All",
    );

    const { classes } = useStyles();
    const dispatch = useDispatch();
    const [updateProduct, { isLoading: isUpdatingProduct }] = useUpdateProductMutation();
    const { t } = useTranslation(["sidepanels", "common"]);

    const { data: workspace } = useCurrentWorkspace();
    const { productAbility } = PermissionService();
    const cannotManageProducts = productAbility(workspace).cannot("manage", "Product");

    const addedModules: AndroidModule[] = product?.customBuildConfig?.modules || [];
    const matchingAddedModules = Utils.filterModules(addedModules, selectedTag);
    const matchingModuleTemplates = Utils.filterModules(Utils.addableModuleTemplates, selectedTag);
    const availableModuleTemplates = Utils.excludeAlreadyAddedModules(matchingModuleTemplates, matchingAddedModules);

    const overlays: Overlay[] = product?.customBuildConfig?.overlays ?? [];
    const isOverlayTemplateVisible = selectedTag === "All" || selectedTag === "Build";

    const allShownCards = [...matchingAddedModules, ...availableModuleTemplates];
    const allTags = Utils.getTags([...addedModules, ...Utils.addableModuleTemplates]);

    // No template card carries the "Added" tag, so once the last module and overlay are gone that
    // chip would leave the panel empty with nothing to add. Send the user back to "All" instead.
    const resetTagWhenNothingAdded = (config: Product["customBuildConfig"]) => {
        if (!config?.modules.length && !config?.overlays?.length) {
            setSelectedTag("All");
        }
    };

    const onDirectAddModule = (module: AndroidModule) => {
        if (!product) {
            return;
        }
        const newProduct = _.cloneDeep(product);
        if (newProduct.customBuildConfig) {
            newProduct.customBuildConfig.modules = [...addedModules, module];
        }
        updateProduct(newProduct);
    };

    const onDirectRemoveModule = (module: AndroidModule) => {
        if (!product) {
            return;
        }
        const newProduct = _.cloneDeep(product);
        if (newProduct.customBuildConfig) {
            newProduct.customBuildConfig.modules = addedModules.filter(
                (m) => !(m.name === module.name && m.type === module.type),
            );
        }
        if (newProduct.customBuildConfig && typeof module.sourceId === "string") {
            delete newProduct.customBuildConfig.sources[module.sourceId];
        }
        resetTagWhenNothingAdded(newProduct.customBuildConfig);
        updateProduct(newProduct);
    };

    const onRemoveOverlay = (index: number) => {
        if (!product) {
            return;
        }
        const newProduct = _.cloneDeep(product);
        if (newProduct.customBuildConfig) {
            newProduct.customBuildConfig.overlays = overlays.filter((_item, itemIndex) => itemIndex !== index);
        }
        resetTagWhenNothingAdded(newProduct.customBuildConfig);
        updateProduct(newProduct);
    };

    const getTooltipTitle = () => {
        if (cannotManageProducts) {
            return t("common:tooltip.cannotManageProducts");
        }
        if (!product) {
            return t("common:tooltip.noProductSelected");
        }
        return undefined;
    };

    const createCards = (modules: AndroidModule[]) => {
        return modules.map((module) => {
            if (module.type === "prebuilt_app") {
                return (
                    <PrebuiltApplicationCard
                        key={module.type + module.name}
                        module={module}
                        onRemove={() => onDirectRemoveModule(module)}
                        onAdd={() => setOpenAddOwnApplicationDialog(true)}
                        isDisabled={cannotManageProducts || !product || isUpdatingProduct}
                        tooltip={getTooltipTitle()}
                    />
                );
            } else if (module.type === "prebuilt_executable") {
                return (
                    <PrebuiltExecutableCard
                        key={module.type + module.name}
                        module={module}
                        onRemove={() => onDirectRemoveModule(module)}
                        onAdd={() => setOpenAddOwnExecutableDialog(true)}
                        isDisabled={cannotManageProducts || !product || isUpdatingProduct}
                        tooltip={getTooltipTitle()}
                    />
                );
            } else if (module.type === "remove_override") {
                return (
                    <RemoveOverrideCard
                        key={module.type + module.name}
                        module={module}
                        onRemove={() => onDirectRemoveModule(module)}
                        onAdd={() => setOpenExcludeSystemPackageDialog(true)}
                        isDisabled={cannotManageProducts || !product || isUpdatingProduct}
                        tooltip={getTooltipTitle()}
                    />
                );
            } else if (module.type === "prebuilt_bootanimation") {
                return (
                    <PrebuiltBootAnimationCard
                        key={module.type}
                        module={module}
                        sources={product?.customBuildConfig?.sources ?? {}}
                        onRemove={() => onDirectRemoveModule(module)}
                        onAdd={() => setOpenBootAnimationDialog(true)}
                        onEdit={() => setOpenBootAnimationDialog(true)}
                        isDisabled={cannotManageProducts || !product || isUpdatingProduct}
                        tooltip={getTooltipTitle()}
                    />
                );
            } else if (module.type === "downloadable_file") {
                return (
                    <DownloadableFileCard
                        key={module.type + module.name}
                        module={module}
                        sources={product?.customBuildConfig?.sources ?? {}}
                        onRemove={() => onDirectRemoveModule(module)}
                        onAdd={() => setOpenAddDownloadableFileDialog(true)}
                        isDisabled={cannotManageProducts || !product || isUpdatingProduct}
                        tooltip={getTooltipTitle()}
                    />
                );
            } else {
                return (
                    <ExistingModuleCard
                        key={module.name}
                        module={module}
                        onRemove={() => onDirectRemoveModule(module)}
                        onDirectAddModule={() => onDirectAddModule(module)}
                        onIncludeSystemPackage={() => setOpenIncludeSystemPackageDialog(true)}
                        isDisabled={cannotManageProducts || !product || isUpdatingProduct}
                        tooltip={getTooltipTitle()}
                    />
                );
            }
        });
    };

    if (!product) {
        return null;
    }

    return (
        <Utils.SearchProvider>
            {({ onChange }) => (
                <React.Fragment>
                    <PaneElement.HeaderBox>
                        <Box className={classes.header}>
                            <Typography variant="h6">{t("modulePanel.title")}</Typography>
                            {product?.customBuildConfig?.productName ? (
                                <Typography variant="body1">{Utils.getProductHeaderTitle(product)}</Typography>
                            ) : null}
                        </Box>
                        <SecondaryButton onClick={() => dispatch(closeDetails())}>
                            <i className="fa-solid fa-xmark" />
                        </SecondaryButton>
                    </PaneElement.HeaderBox>
                    <Box className={classes.searchContainerBox}>
                        <PaneElement.Header>{t("sidepanels:modulePanel.header")}</PaneElement.Header>
                        <Box className={classes.searchContainer}>
                            <QueryBuilderField
                                placeholder={t("common:button.search")}
                                onChange={onChange}
                                onClear={() => onChange("")}
                            />

                            <Box className={classes.tagsContainer}>
                                {allTags.map((tag) => (
                                    <Chip
                                        key={tag}
                                        label={tag}
                                        color="primary"
                                        size="small"
                                        variant={selectedTag === tag ? "filled" : "outlined"}
                                        onClick={() => setSelectedTag(tag)}
                                    />
                                ))}
                            </Box>
                        </Box>
                    </Box>

                    <PaneElement.Container className={classes.contentBox}>
                        <Box className={classes.gridContainer}>
                            {createCards(addedModules)}
                            {overlays.map((overlay, index) => (
                                <CustomOverlayCard
                                    key={`${overlay.targetPackage}|${overlay.resourcePath}|${overlay.resourceType}|${overlay.resourceName}`}
                                    overlay={overlay}
                                    onRemove={() => onRemoveOverlay(index)}
                                    isDisabled={cannotManageProducts || !product || isUpdatingProduct}
                                    tooltip={getTooltipTitle()}
                                />
                            ))}
                        </Box>
                        {(addedModules.length || overlays.length) &&
                        (availableModuleTemplates.length || isOverlayTemplateVisible) ? (
                            <Box className={classes.separator} />
                        ) : null}
                        <Box className={classes.gridContainer}>
                            {createCards(availableModuleTemplates)}
                            {isOverlayTemplateVisible ? (
                                <CustomOverlayCard
                                    onAdd={() => setOpenAddOverlayDialog(true)}
                                    isDisabled={cannotManageProducts || !product || isUpdatingProduct}
                                    tooltip={getTooltipTitle()}
                                />
                            ) : null}
                        </Box>
                        {!allShownCards.length && !overlays.length && !isOverlayTemplateVisible ? (
                            <PaneElement.NoData message={t("modulePanel.noModulesFound")} />
                        ) : null}

                        <AddApplicationDialog
                            product={product}
                            open={openAddOwnApplicationDialog}
                            onToggle={setOpenAddOwnApplicationDialog}
                        />
                        <BootAnimationDialog
                            product={product}
                            open={openBootAnimationDialog}
                            onToggle={setOpenBootAnimationDialog}
                        />
                        <AddDownloadableFileDialog
                            product={product}
                            open={openAddDownloadableFileDialog}
                            onToggle={setOpenAddDownloadableFileDialog}
                        />
                        <AddExecutableDialog
                            product={product}
                            open={openAddOwnExecutableDialog}
                            onToggle={setOpenAddOwnExecutableDialog}
                        />
                        <AddOverlayDialog product={product} open={openAddOverlayDialog} onToggle={setOpenAddOverlayDialog} />
                        <ExcludeModuleDialog
                            product={product}
                            open={openExcludeSystemPackageDialog}
                            onToggle={setOpenExcludeSystemPackageDialog}
                        />
                        <IncludeModuleDialog
                            product={product}
                            open={openIncludeSystemPackageDialog}
                            onToggle={setOpenIncludeSystemPackageDialog}
                        />
                    </PaneElement.Container>
                </React.Fragment>
            )}
        </Utils.SearchProvider>
    );
};
